import React, { useEffect, useRef, useState } from 'react';
import { Bell, CheckCircle, ChevronDown, ChevronUp, Construction, Eye, Sun, type LucideIcon } from 'lucide-react';
import {
  BRIEFING_HIGHLIGHT_PREVIEW,
  type BriefingBucketFilter,
  type BriefingBucketId,
  type RadarBriefingHighlight,
  type RadarBriefingModel,
} from '../../domain/radarThreads';
import { BrandIcon, SlackRichText } from '../../ui/common';
import { ViewMode } from '../../domain/types';

interface RadarBriefingBannerProps {
  briefing: RadarBriefingModel;
  selectedBucket?: BriefingBucketFilter;
  showAllHighlights?: boolean;
  onSelectBucket?: (id: BriefingBucketId) => void;
  onOpenHighlight?: (view: ViewMode, id: string) => void;
  onAddHighlightToContext?: (item: RadarBriefingHighlight) => void;
  onViewFullBriefing?: () => void;
}

const TONE_COLORS: Record<RadarBriefingModel['buckets'][number]['tone'], string> = {
  bad: 'var(--z-bad)',
  warn: 'var(--z-warn)',
  accent: '#3B82F6',
  ok: 'var(--z-ok)',
};

function cleanBriefingText(value: string): string {
  const cleaned = value.replace(/^[«“”"'\s]+|[»“”"'\s]+$/g, '').trim();
  return cleaned.replace(/<(https?:\/\/[^>\s]+)(?=\s|$)/g, '$1');
}

function BucketIcon({ id, color }: { id: BriefingBucketId; color: string }): React.ReactElement {
  const icons: Record<BriefingBucketId, LucideIcon> = {
    'require-attention': Bell,
    watching: Eye,
    blockers: Construction,
    'under-control': CheckCircle,
  };
  const Icon = icons[id];
  return <Icon size={22} color={color} aria-hidden />;
}

export const RadarBriefingBanner: React.FC<RadarBriefingBannerProps> = ({
  briefing,
  selectedBucket = 'all',
  showAllHighlights = false,
  onSelectBucket,
  onOpenHighlight,
  onAddHighlightToContext,
  onViewFullBriefing,
}) => {
  const highlights = showAllHighlights
    ? briefing.highlights
    : briefing.highlights.slice(0, BRIEFING_HIGHLIGHT_PREVIEW);
  const firstHighlightId = highlights[0]?.id ?? null;
  const visibleHighlightIds = JSON.stringify(highlights.map((item) => item.id));
  const hadVisibleHighlightsRef = useRef(firstHighlightId !== null);
  const [expandedHighlightId, setExpandedHighlightId] = useState<string | null>(firstHighlightId);

  useEffect(() => {
    const availableIds = new Set<string>(JSON.parse(visibleHighlightIds) as string[]);
    if (!firstHighlightId) {
      hadVisibleHighlightsRef.current = false;
      setExpandedHighlightId(null);
      return;
    }
    const shouldExpandFirst = !hadVisibleHighlightsRef.current;
    setExpandedHighlightId((current) => {
      if (shouldExpandFirst || (current && !availableIds.has(current))) {
        return firstHighlightId;
      }
      return current;
    });
    hadVisibleHighlightsRef.current = true;
  }, [firstHighlightId, visibleHighlightIds]);

  return (
    <section
      data-testid="radar-briefing-banner"
      className="z-radar-briefing"
      aria-label="Briefing de hoy"
    >
      <aside className="z-radar-briefing-metrics" aria-label="Métricas de atención">
        <div className="z-radar-briefing-buckets" data-testid="radar-briefing-buckets">
          {briefing.buckets.map((bucket, index) => {
            const selected = selectedBucket === bucket.id;
            const visuallyActive = selected || (selectedBucket === 'all' && index === 0);
            return (
              <button
                key={bucket.id}
                type="button"
                className={`z-radar-briefing-bucket${visuallyActive ? ' is-active' : ''}`}
                data-tone={bucket.tone}
                data-testid={`radar-briefing-bucket-${bucket.id}`}
                aria-pressed={selected}
                aria-controls="radar-contextos"
                aria-label={`${bucket.count} ${bucket.title}`}
                onClick={() => onSelectBucket?.(bucket.id)}
              >
                <span className="z-radar-briefing-bucket-icon" style={{ color: TONE_COLORS[bucket.tone] }}><BucketIcon id={bucket.id} color={TONE_COLORS[bucket.tone]} /></span>
                <span className="z-radar-briefing-bucket-copy">
                  <strong className="z-radar-briefing-bucket-count" style={{ color: TONE_COLORS[bucket.tone] }}>{bucket.count}</strong>
                  <span className="z-radar-briefing-bucket-title">{bucket.title}</span>
                  <span className="z-radar-briefing-bucket-sub">{bucket.subtitle}</span>
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      <div className="z-radar-briefing-content">
        <div className="z-radar-briefing-header">
          <div className="z-radar-briefing-title-row">
            <Sun size={18} aria-hidden />
            <h2>Briefing de hoy</h2>
            <span className="z-radar-briefing-age">{briefing.updatedLabel}</span>
          </div>
          <p className="z-radar-briefing-sentence">{briefing.attentionSentence}</p>
        </div>

        <div className="z-radar-briefing-side">
        <h3>Asuntos que se te pueden pasar</h3>
        <ul className="z-radar-briefing-highlights">
          {highlights.length === 0 ? (
            <li className="z-radar-briefing-empty">Nada urgente en este momento</li>
          ) : (
            highlights.map((item) => {
              const expanded = expandedHighlightId === item.id;
              return (
              <li key={item.id} className={expanded ? 'is-expanded' : undefined}>
                {expanded ? (
                  <article className="z-radar-briefing-highlight-detail" data-testid="radar-briefing-highlight-detail">
                    <button
                      type="button"
                      className="z-radar-briefing-detail-toggle"
                      aria-expanded="true"
                      onClick={() => setExpandedHighlightId(null)}
                    >
                      <BrandIcon source={item.source} size={18} />
                      <SlackRichText
                        as="span"
                        className="z-radar-briefing-detail-title"
                        text={cleanBriefingText(item.title)}
                      />
                      <span className="z-radar-briefing-highlight-age">{item.age}</span>
                      <ChevronUp size={15} aria-hidden />
                    </button>
                    <SlackRichText
                      as="div"
                      className="z-radar-briefing-detail-body"
                      text={cleanBriefingText(item.detail)}
                    />
                    <div className="z-radar-briefing-detail-actions">
                      <button type="button" className="z-radar-briefing-detail-primary" onClick={() => onOpenHighlight?.(item.targetView, item.id)}>
                        Abrir asunto <span aria-hidden>→</span>
                      </button>
                      {onAddHighlightToContext && (
                        <button
                          type="button"
                          className="z-radar-briefing-detail-secondary"
                          data-testid={`radar-briefing-add-context-${item.id}`}
                          onClick={() => onAddHighlightToContext(item)}
                        >
                          Agregar al contexto
                        </button>
                      )}
                    </div>
                  </article>
                ) : (
                <div className="z-radar-briefing-highlight-row">
                  <button
                    type="button"
                    className="z-radar-briefing-highlight"
                    aria-expanded="false"
                    onClick={() => setExpandedHighlightId(item.id)}
                  >
                    <BrandIcon source={item.source} size={16} />
                    <SlackRichText
                      as="span"
                      className="z-radar-briefing-highlight-title"
                      text={cleanBriefingText(item.title)}
                    />
                    <span className="z-radar-briefing-highlight-age">{item.age}</span>
                    <ChevronDown size={14} aria-hidden />
                  </button>
                  {onAddHighlightToContext && (
                    <button
                      type="button"
                      className="z-radar-briefing-context-action"
                      data-testid={`radar-briefing-add-context-${item.id}`}
                      onClick={() => onAddHighlightToContext(item)}
                    >
                      Agregar al contexto
                    </button>
                  )}
                </div>
                )}
              </li>
              );
            })
          )}
        </ul>
        <button
          type="button"
          className="z-radar-briefing-cta"
          data-testid="radar-briefing-view-all"
          aria-controls="radar-contextos"
          onClick={onViewFullBriefing}
        >
          Ver más
        </button>
        </div>
      </div>
    </section>
  );
};
